import { DestroyRef, Injectable } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { TabletIdentifierService } from '@app-services/tablet-identifier/tablet-identifier.service'
import { BehaviorSubject, filter, firstValueFrom, map, Observable, startWith, switchMap, tap, retry } from 'rxjs'
import {
    TabletMessageFragment,
    TabletStatusEnum,
    TabletStatusQueryService,
    TabletSubscriptionService,
} from '../../generated/graphql'

@Injectable({
    providedIn: 'root',
})
export class TabletsService {

    private currentTabletMessage$$ = new BehaviorSubject<null | TabletMessageFragment>(null)
    public currentTabletMessage$ = this.currentTabletMessage$$.asObservable()
    public isConfigured$: Observable<TabletStatusEnum>

    constructor(
        private readonly tabletSubscriptionService: TabletSubscriptionService,
        private readonly tabletIdentifierService: TabletIdentifierService,
        private readonly destroyRef: DestroyRef,
        private readonly tabletStatusQueryService: TabletStatusQueryService,
        private readonly router: Router,
    ) {
    }

    public async initialize(): Promise<void> {
        this.tabletIdentifierService.identifier$.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter((identifier) => identifier !== null),
            switchMap((identifier) => {
                return this.listenToTabletUpdates(identifier)
            }),
            tap((tabletMessage) => {
                // Save message
                this.currentTabletMessage$$.next(tabletMessage)

                // Check if tablet is unregistered if so start onboarding flow again
                if (tabletMessage?.status === TabletStatusEnum.Unregistered) {
                    this.resetTabletMessage()
                    this.tabletIdentifierService.regenerateIdentifier()
                    this.router.navigate(['/onboarding'])
                }
            }),
        ).subscribe()

        this.isConfigured$ = this.tabletIdentifierService.identifier$.pipe(
            startWith(TabletStatusEnum.Unregistered),
            filter((identifier) => identifier !== null),
            switchMap(async (identifier) => {
                return this.getTabletStatus(identifier)
            }),
        )
    }

    public async getTabletStatus(identifier: string) {
        return firstValueFrom(this.tabletStatusQueryService.fetch({
            identifier,
        },
        {
            fetchPolicy: 'network-only',
        },
        ).pipe(
            map((response) => response.data.tabletStatus),
        ))
    }

    public resetTabletMessage() {
        this.currentTabletMessage$$.next(null)
    }

    public listenToTabletUpdates(identifier: string) {
        return this.tabletSubscriptionService.subscribe({
            identifier,
        }).pipe(
            retry(),
            map((result) => result.data?.tabletSubscription ?? null),
        )
    }
}
